import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, Link, MessageSquare, PieChart, User } from 'lucide-react';

export const voiceSurvey: Playbook = {
  meta: {
    id: 'voice-survey',
    slug: 'voice-survey',
    categoryId: 'gather-insights',
    name: 'The Voice Survey Link',
    tagline: 'Send conversational survey genies.',
    description: 'Replace boring survey forms with voice Genie links. Send via email or SMS for market research, NPS scores, or customer feedback.',
    strategy: 'Replace boring survey forms with voice Genie links. Send via email or SMS for market research, NPS scores, or customer feedback.',
    benefits: [
      'Higher survey completion rates',
      'More detailed responses',
      'Better user experience',
      'Richer qualitative data'
    ],
    targetAudience: ['Market researchers', 'Product teams', 'Customer experience teams'],
    estimatedSetupTime: '22 minutes',
    difficulty: 'beginner',
    tags: ['surveys', 'market-research', 'nps', 'feedback'],
    isFeatured: false
  },
  seo: {
    metaTitle: 'Voice Survey Playbook - Conversational Genie Market Research',
    metaDescription: 'Replace static surveys with engaging Genie conversations. Higher completion rates and better insights via voice survey links.',
    keywords: ['voice surveys', 'conversational surveys', 'market research', 'Genie feedback', 'survey completion']
  },
  demoConfig: {
    triggerType: 'immediate',
    message: "Hi! Thanks for clicking our survey link. This will just take 2-3 minutes and your feedback really helps us improve. Ready to start?",
    position: 'center'
  },
  demoScript: [
    { speaker: 'agent', message: "Hi! Thanks for clicking our survey link. This takes just 2 minutes. Ready?" },
    { speaker: 'user', message: "Sure, go ahead" },
    { speaker: 'agent', message: "Great! First, how satisfied are you with your recent purchase on a scale of 1-10?" },
    { speaker: 'user', message: "I'd say 8" },
    { speaker: 'agent', message: "That's good to hear! What could we do to make it a perfect 10?" }
  ],
  templateConfig: {
    firstMessage: "Hi! Thanks for clicking our survey link about [TOPIC]. This will be quick - ready to share your thoughts?",
    systemPrompt: "You are a friendly survey Genie accessed via a link. Make the survey conversational and engaging. Listen actively, probe for insights, but keep it brief. Thank them for their time.",
    tools: ['survey_logic', 'response_recording', 'data_analytics', 'skip_logic', 'completion_tracking']
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'User receives survey link',
      description: 'Customer gets email or SMS with personalized survey link instead of traditional form'
    },
    {
      type: 'genie',
      icon: Link,
      genieIcon: 'genie-lamp',
      label: 'Clicks to start conversation',
      description: 'User clicks link and immediately begins conversing with the survey Genie'
    },
    {
      type: 'action',
      icon: MessageSquare,
      genieIcon: 'settings',
      label: 'Engages in natural dialogue',
      description: 'Genie asks survey questions conversationally, adapting based on previous responses'
    },
    {
      type: 'action',
      icon: Bot,
      genieIcon: 'target',
      label: 'Probes for deeper insights',
      description: 'Follows up with clarifying questions and explores interesting responses further'
    },
    {
      type: 'action',
      icon: PieChart,
      genieIcon: 'database',
      label: 'Records structured data',
      description: 'Captures both quantitative scores and rich qualitative feedback'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'trending-up',
      label: 'Rich feedback collected',
      description: 'Higher completion rate with more detailed and actionable insights'
    }
  ],
  metrics: {
    conversionRate: '67%',
    responseTime: '<1 second',
    satisfactionScore: '4.5/5',
    costSavings: '$15k/survey',
    timeToValue: '3 minutes'
  },
  testimonials: [
    {
      companyName: 'InsightPro Research',
      authorName: 'Dr. Amanda Foster',
      authorTitle: 'Research Director',
      quote: 'Voice survey links increased our completion rate from 12% to 47% and provided much richer qualitative data.',
      metrics: '47% completion rate vs 12% traditional'
    }
  ],
  resources: [
    {
      type: 'template',
      title: 'Conversational Survey Scripts',
      description: 'Question flows that work well in conversational format',
      url: '/templates/conversational-surveys'
    },
    {
      type: 'guide',
      title: 'Survey Data Analysis',
      description: 'How to analyze and extract insights from conversational survey data',
      url: '/docs/survey-data-analysis'
    }
  ]
};